import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Bed, Bath, Maximize, Heart } from 'lucide-react';
import { motion } from 'framer-motion';
import { formatPrice, formatArea, getOperationLabel } from '@/utils/formatters';
import { Badge } from '@/components/ui/badge';

const PropertyCard = ({ property }) => {
  const mainImage =
    property.images?.[0] ||
    'https://images.unsplash.com/photo-1706808849827-7366c098b317?crop=entropy&cs=srgb&fm=jpg&q=85';

  // Use slug if available, fallback to id for backward compatibility
  const propertyUrl = property.slug
    ? `/inmueble/${property.slug}`
    : `/propiedad/${property.id}`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      whileHover={{ y: -4 }}
      className='group bg-white rounded-2xl overflow-hidden border border-slate-100 hover:border-primary/20 hover:shadow-xl hover:shadow-slate-200/50 transition-all duration-300'
      data-testid={`property-card-${property.id}`}
    >
      <Link to={propertyUrl}>
        {/* Image */}
        <div className='relative h-56 overflow-hidden'>
          <img
            src={mainImage}
            alt={property.title}
            className='w-full h-full object-cover group-hover:scale-110 transition-transform duration-500'
          />
          <div className='absolute top-4 left-4'>
            <Badge
              className={`${
                property.operation === 'SALE'
                  ? 'bg-primary hover:bg-primary-hover'
                  : 'bg-secondary hover:bg-secondary-hover'
              } text-white font-semibold`}
            >
              {getOperationLabel(property.operation)}
            </Badge>
          </div>
          {property.is_featured && (
            <div className='absolute top-4 right-4'>
              <Badge className='bg-accent hover:bg-accent-hover text-white font-semibold'>
                Destacado
              </Badge>
            </div>
          )}
          <button
            className='absolute bottom-4 right-4 p-2 bg-white/90 rounded-full hover:bg-white transition-colors duration-200'
            onClick={(e) => {
              e.preventDefault();
              console.log('Add to favorites:', property.id);
            }}
            data-testid={`favorite-button-${property.id}`}
          >
            <Heart className='w-5 h-5 text-slate-600' />
          </button>
        </div>

        {/* Content */}
        <div className='p-6'>
          {/* Price */}
          <div className='mb-3'>
            <p className='text-3xl font-heading font-bold text-slate-900'>
              {formatPrice(property.price)}
            </p>
            {property.operation === 'RENT' && (
              <p className='text-sm text-slate-500 font-medium'>por mes</p>
            )}
          </div>

          {/* Title */}
          <h3 className='text-lg font-heading font-semibold text-slate-900 mb-2 line-clamp-2 group-hover:text-primary transition-colors'>
            {property.title}
          </h3>

          {/* Location */}
          <div className='flex items-center text-slate-600 mb-4'>
            <MapPin className='w-4 h-4 mr-1 flex-shrink-0' />
            <span className='text-sm truncate'>
              {property.neighborhood ? `${property.neighborhood}, ` : ''}
              {property.city}
            </span>
          </div>

          {/* Features */}
          <div className='flex items-center justify-between text-sm text-slate-600 pt-4 border-t border-slate-100'>
            <div className='flex items-center space-x-4'>
              {property.rooms && (
                <div
                  className='flex items-center space-x-1'
                  data-testid={`rooms-${property.id}`}
                >
                  <Bed className='w-4 h-4' />
                  <span>{property.rooms}</span>
                </div>
              )}
              {property.bathrooms && (
                <div
                  className='flex items-center space-x-1'
                  data-testid={`bathrooms-${property.id}`}
                >
                  <Bath className='w-4 h-4' />
                  <span>{property.bathrooms}</span>
                </div>
              )}
              <div
                className='flex items-center space-x-1'
                data-testid={`area-${property.id}`}
              >
                <Maximize className='w-4 h-4' />
                <span>{formatArea(property.area_m2)}</span>
              </div>
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default PropertyCard;
